import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Button, Checklist, Field } from '@itds/elements/index.js';
import {
  GUARANTEED_UNIVERSAL_LIFE_PRODUCTS,
  OTHER_PRODUCTS,
  ROP_PRODUCTS,
  TERM_LIFE_INSURANCE_PRODUCTS,
  PRODUCT_TYPE_DETAILS_BY_PRODUCT_TYPE
} from '@itds/Constants';

const LimitProductTypes = ({ bga_quoter, quoter, handleSave }) => {
  const all_product_types = [
    ...TERM_LIFE_INSURANCE_PRODUCTS,
    ...GUARANTEED_UNIVERSAL_LIFE_PRODUCTS.map(p => `GUL:${p}`),
    ...PRODUCT_TYPE_DETAILS_BY_PRODUCT_TYPE.IUL.map(p => `IUL:${p}`),
    ...ROP_PRODUCTS,
    ...OTHER_PRODUCTS
  ];
  // Use quoter if present (Universal Quoter), else fallback to bga_quoter
  const currentProductTypes =
    (quoter && quoter.product_types) ||
    (bga_quoter && bga_quoter.product_types) ||
    all_product_types;

  // Convert legacy product types to prefixed format for display
  const convertToDisplayFormat = productTypes => {
    return productTypes.map(product => {
      // If already prefixed, return as is
      if (product.includes(':')) return product;

      // Check if it belongs to GUL
      if (GUARANTEED_UNIVERSAL_LIFE_PRODUCTS.includes(product)) {
        return `GUL:${product}`;
      }
      // Check if it belongs to IUL
      if (PRODUCT_TYPE_DETAILS_BY_PRODUCT_TYPE.IUL.includes(product)) {
        return `IUL:${product}`;
      }
      // Return as is for other product types
      return product;
    });
  };

  // Convert prefixed format back to original format for saving
  const convertToSaveFormat = productTypes => {
    return productTypes.map(product => {
      if (product.startsWith('GUL:') || product.startsWith('IUL:')) {
        const value = product.split(':')[1];

        return value.replace(/\s*\(\s*/g, ' (').replace(/\s*\)/g, ')');
      }
      return product.replace(/\s*\(\s*/g, ' (').replace(/\s*\)/g, ')');
    });
  };

  const initialProductTypes = convertToDisplayFormat(currentProductTypes);

  const [data, setData] = useState({
    product_types: initialProductTypes
  });
  useEffect(() => {
    setData({ product_types: convertToDisplayFormat(currentProductTypes) });
  }, [quoter, bga_quoter]);

  const handleChecklistChange = (name, selected) => {
    setData({ ...data, [name]: selected });
  };

  const save = () => {
    const normalizedTypes = convertToSaveFormat(data.product_types);
    // Remove duplicates
    const uniqueTypes = [...new Set(normalizedTypes)];
    const dataToSave = {
      product_types: uniqueTypes
    };
    handleSave(dataToSave);
  };
  const selectAll = () => {
    if (data.product_types.length === all_product_types.length) {
      setData({
        ...data,
        product_types: []
      });
    } else {
      setData({
        ...data,
        product_types: all_product_types
      });
    }
  };

  return (
    <>
      <div style={{ display: 'flex', marginBottom: 30 }}>
        <Button inline={true} onClick={selectAll} small={true}>
          {data.product_types.length === all_product_types.length ? 'Unselect All' : 'Select All'}
        </Button>
      </div>

      <div style={{ display: 'flex', marginBottom: 30 }}>
        <div style={{ width: '25%' }}>
          <Field label='Term Life'>
            <Checklist
              onChange={handleChecklistChange.bind(null, 'product_types')}
              options={TERM_LIFE_INSURANCE_PRODUCTS.map(p => ({ value: p }))}
              selected={data.product_types || []}
            />
          </Field>
        </div>{' '}
        <div style={{ width: '25%' }}>
          <Field label='Guaranteed Universal Life'>
            <Checklist
              onChange={handleChecklistChange.bind(null, 'product_types')}
              options={GUARANTEED_UNIVERSAL_LIFE_PRODUCTS.map(p => ({
                value: `GUL:${p}`,
                label: p
              }))}
              selected={data.product_types || []}
            />
          </Field>
        </div>
        <div style={{ width: '25%' }}>
          <Field label='Indexed Universal Life'>
            <Checklist
              onChange={handleChecklistChange.bind(null, 'product_types')}
              options={PRODUCT_TYPE_DETAILS_BY_PRODUCT_TYPE.IUL.map(p => ({
                value: `IUL:${p}`,
                label: p
              }))}
              selected={data.product_types || []}
            />
          </Field>
        </div>
        <div style={{ width: '25%' }}>
          <Field label='Return of Premium'>
            <Checklist
              onChange={handleChecklistChange.bind(null, 'product_types')}
              options={ROP_PRODUCTS.map(p => ({ value: p }))}
              selected={data.product_types || []}
            />
          </Field>
        </div>
        <div style={{ width: '25%' }}>
          <Field label='Other'>
            <Checklist
              onChange={handleChecklistChange.bind(null, 'product_types')}
              options={OTHER_PRODUCTS.map(p => ({ value: p }))}
              selected={data.product_types || []}
            />
          </Field>
        </div>
      </div>

      <Button disabled={!data.product_types.length} onClick={save}>
        Save Changes
      </Button>
    </>
  );
};

LimitProductTypes.propTypes = {
  bga_quoter: PropTypes.object, // for universal quoter
  handleSave: PropTypes.func, // legacy
  quoter: PropTypes.object
};

export default LimitProductTypes;
